import moment from 'moment';
import { alpha } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '../../ui/core';
import { Muted } from './parts';
import { formatNum, formatShare, formatTs, shadowTooltip } from './sdpFormatters';

// Cities × departure days at a glance: each cell is the city-day's current
// multiplier → the target it is stepping toward, and its unclaimed / board
// (unclaimed share), on the colour of its kind (cellKind below).

const DAY = 'YYYY-MM-DD';
// A guard that holds pay (safe / frozen) says more about the cell than the
// demand label does, so it wins the colour. explore_off only stops the
// experiment, and pay still follows demand, so the label keeps it.
const OVERRIDING_GUARDS = ['safe', 'frozen'];

// The reference's `min_board` (fixed at 5 by the refit; the admin API doesn't
// expose reference params). Since HITCH-5561 a smaller board is padded up to it
// and still labelled, so a far-out 1 / 1 board reads `trouble`: the grid greys
// these out instead, and the label stays in the hover detail.
export const MIN_BOARD = 5;

// Each kind gets its own hue. The table pills share theme keys across meanings
// (trouble / safe_mode / guard safe are all `error`), which a grid of
// colour-only cells can't afford.
export const KIND_COLORS = {
  trouble: (theme) => theme.palette.error?.main,
  ok: (theme) => theme.palette.success?.main,
  ahead: (theme) => theme.palette.accent?.main,
  frozen: (theme) => theme.palette.warning?.main,
  safe: (theme) => (theme.palette.mode === 'dark' ? '#B69CFF' : '#7A4FD0'),
  small_board: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : '#64748B'),
};

// What a cell is coloured as, or null for no fill. Guards first (they hold
// pay whatever the board), then an emptied board (today, once every trip has
// departed: its last label is stale), then a board under min_board, then the
// demand label.
export const cellKind = (row) => {
  if (!row) return null;
  if (row.guardState === 'safe' || row.label === 'safe_mode') return 'safe';
  if (row.guardState === 'frozen') return 'frozen';
  if (!row.board) return 'empty_board';
  if (row.board < MIN_BOARD || row.label === 'below_min_board') return 'small_board';
  return KIND_COLORS[row.label] ? row.label : null;
};

// Two decimals, or "—" when the row has no multiplier yet.
const formatMult = (value) => (typeof value === 'number' ? value.toFixed(2) : '—');

// Unclaimed share of the board (the API's `share` is the claimed side).
export const unclaimedShare = (row) => (row && row.board > 0 ? row.unclaimed / row.board : null);

// A month of columns still reads at laptop width with a horizontal scroll;
// a hand-typed multi-year range would otherwise render thousands of cells
// (and does so before its own fetch has even returned).
export const MAX_GRID_DAYS = 31;

// Inclusive list of YYYY-MM-DD days, at most `limit` of them.
export const daysBetween = (from, to, limit = Infinity) => {
  const days = [];
  const end = moment(to, DAY);
  for (let d = moment(from, DAY); !d.isAfter(end, 'day') && days.length < limit; d.add(1, 'day')) days.push(d.format(DAY));
  return days;
};

// Columns run today → the API's horizon unless the filter says otherwise.
// Without a `from`, yesterday (which the API includes by default) is dropped.
// `truncated` says the range ran past MAX_GRID_DAYS and was cut.
export const gridDays = ({ from, to, dataFrom, dataTo, today }) => {
  const end = to || dataTo;
  if (!end) return { days: [], truncated: false };
  let start = from || (today > dataFrom ? today : dataFrom);
  if (start > end) start = from || dataFrom;
  const days = daysBetween(start, end, MAX_GRID_DAYS + 1);
  return { days: days.slice(0, MAX_GRID_DAYS), truncated: days.length > MAX_GRID_DAYS };
};

// Owned (active) cities by default; log_only cities only when asked. Falls
// back to the rows' own cities while Status hasn't loaded.
export const gridCities = ({ cities, rows, includeLogOnly, cityId }) => {
  const source = cities.length
    ? cities
    : [...new Map(rows.map((r) => [r.cityId, { cityId: r.cityId, cityName: r.cityName, mode: r.mode }])).values()];
  if (cityId) return source.filter((c) => c.cityId === cityId);
  return source.filter((c) => c.mode === 'active' || (includeLogOnly && c.mode === 'log_only'));
};

const isWeekend = (day) => [0, 6].includes(moment(day, DAY).day());

const LEGEND = [
  { kind: 'trouble', text: 'trouble' },
  { kind: 'ok', text: 'ok' },
  { kind: 'ahead', text: 'ahead' },
  { kind: 'safe', text: 'guard safe (safe_mode)' },
  { kind: 'frozen', text: 'guard frozen' },
  { kind: 'small_board', text: `board < min_board (${MIN_BOARD})` },
  { kind: 'empty_board', text: 'empty board (0 / 0)' },
];

// A strong tint of the kind's colour so a cell reads at a glance; no fill for
// an empty board or an unknown label.
const fill = (kind) => (theme) => {
  const color = KIND_COLORS[kind] && KIND_COLORS[kind](theme);
  if (color) return alpha(color, theme.palette.mode === 'dark' ? 0.3 : 0.22);
  return theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
};

// log_only days: hatched over the state colour, like a watermark that says
// "computed, never paid".
const HATCH = (theme) =>
  `repeating-linear-gradient(135deg, transparent 0 5px, ${
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'
  } 5px 6px)`;

const Swatch = ({ kind, shadow = false }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 14,
      height: 14,
      borderRadius: '3px',
      verticalAlign: 'middle',
      mr: '6px',
      backgroundColor: fill(kind),
      backgroundImage: shadow ? HATCH : 'none',
      border: (theme) => `1px ${shadow ? 'dashed' : 'solid'} ${theme.palette.divider}`,
    }}
  />
);

export const Legend = () => (
  <Box data-testid="grid-legend" sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', alignItems: 'center' }}>
    {LEGEND.map((item) => (
      <Typography key={item.kind} component="span" sx={{ fontSize: '12px' }}>
        <Swatch kind={item.kind} />
        {item.text}
      </Typography>
    ))}
    <Typography component="span" sx={{ fontSize: '12px', fontStyle: 'italic' }}>
      <Swatch kind={null} shadow />
      shadow (log_only, never paid)
    </Typography>
    <Typography component="span" color="text.secondary" sx={{ fontSize: '12px' }}>
      — no state
    </Typography>
    <Typography component="span" color="text.secondary" sx={{ fontSize: '12px' }}>
      cell: current → target multiplier, then unclaimed / board
    </Typography>
  </Box>
);

const CellDetail = ({ row, cityName }) => {
  const d = row.latestDecision;
  const lines = [
    `${cityName} · ${row.departureDate}`,
    `label ${row.label}${OVERRIDING_GUARDS.includes(row.guardState) ? ` · guard ${row.guardState}` : ''}${
      row.guardReason ? ` (${row.guardReason})` : ''
    }`,
    `unclaimed / board ${row.unclaimed} / ${row.board} (${formatShare(unclaimedShare(row))})`,
    `need ${formatNum(d && d.need)}`,
    `current ${formatNum(row.currentMult)} → target ${formatNum(row.targetMult)}`,
    `arm ${row.ditherArm ?? '—'}`,
    `read bucket ${(d && d.readBucket) || '—'}`,
    `updated ${formatTs(row.updatedAt)}`,
  ];
  if (row.paid === false) lines.push(`shadow: ${shadowTooltip('log_only')}`);
  if (row.paid == null) lines.push('not priced');
  return (
    <Box>
      {lines.map((line) => (
        <Box key={line} sx={{ fontSize: '12px' }}>
          {line}
        </Box>
      ))}
    </Box>
  );
};

const cellSx = { px: '6px', py: '4px', textAlign: 'center', minWidth: 80, height: 40, boxSizing: 'border-box' };

const GridCell = ({ row, cityName, day, today, onSelect }) => {
  if (!row) {
    return (
      <Box component="td" data-testid={`grid-cell-${day}`} data-empty sx={{ ...cellSx, color: 'text.secondary' }}>
        —
      </Box>
    );
  }
  const kind = cellKind(row);
  const shadow = row.paid === false;
  const guard = OVERRIDING_GUARDS.includes(row.guardState) ? row.guardState : null;
  // currentMult is what's paid; each tick steps it toward targetMult.
  const mult = formatMult(row.currentMult);
  const target = formatMult(row.targetMult);
  // Same shape as the table's "Unclaimed / board (share)" column.
  const unclaimed = `${row.unclaimed ?? '—'} / ${row.board ?? '—'} (${formatShare(unclaimedShare(row))})`;
  return (
    <Box
      component="td"
      data-testid={`grid-cell-${day}`}
      data-kind={kind || 'neutral'}
      data-shadow={shadow}
      sx={{
        ...cellSx,
        p: 0,
        backgroundColor: fill(kind),
        backgroundImage: shadow ? HATCH : 'none',
        outline: day === today ? (theme) => `2px solid ${theme.palette.text.primary}` : 'none',
        outlineOffset: '-2px',
      }}
    >
      {/* A real button so the cell is reachable by keyboard: focus shows the
          tooltip (MUI opens it on focus too) and Enter opens the table. */}
      <Tooltip title={<CellDetail row={row} cityName={cityName} />} arrow placement="top">
        <Box
          component="button"
          type="button"
          data-testid={`grid-button-${day}`}
          aria-label={`${cityName} ${day}: ${mult} → target ${target}, ${guard ? `guard ${guard}` : row.label}, unclaimed ${unclaimed}${
            shadow ? ', shadow' : ''
          }`}
          onClick={onSelect ? () => onSelect(row) : undefined}
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            p: '4px 6px',
            border: 'none',
            background: 'none',
            color: 'inherit',
            font: 'inherit',
            cursor: onSelect ? 'pointer' : 'default',
            '&:hover': { backgroundColor: 'action.hover' },
            '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: '-2px' },
          }}
        >
          <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', fontStyle: shadow ? 'italic' : 'normal' }}>
            <Typography component="span" sx={{ fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {mult}
            </Typography>
            <Typography
              component="span"
              data-testid={`grid-target-${day}`}
              sx={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums', opacity: 0.8, ml: '3px' }}
            >
              {`→ ${target}`}
            </Typography>
          </Box>
          <Typography
            component="span"
            data-testid={`grid-unclaimed-${day}`}
            sx={{
              display: 'block',
              fontSize: '11px',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
              opacity: 0.8,
            }}
          >
            {unclaimed}
          </Typography>
          {guard && (
            <Typography component="span" sx={{ display: 'block', fontSize: '10px', lineHeight: 1.1, fontWeight: 600 }}>
              {guard}
            </Typography>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

const headSx = {
  px: '6px',
  py: '4px',
  fontSize: '12px',
  fontWeight: 500,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
};

const DayHeader = ({ day, today }) => {
  const m = moment(day, DAY);
  const weekend = isWeekend(day);
  const isToday = day === today;
  return (
    <Box
      component="th"
      data-testid={`grid-day-${day}`}
      data-today={isToday}
      data-weekend={weekend}
      sx={{
        ...headSx,
        fontWeight: isToday ? 700 : 500,
        backgroundColor: weekend ? (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
      }}
    >
      <Box sx={{ color: weekend ? 'text.primary' : 'text.secondary' }}>{isToday ? 'Today' : m.format('ddd')}</Box>
      <Box>{m.format('MMM D')}</Box>
    </Box>
  );
};

const StateGrid = ({ rows, cities, days, today, onSelect }) => {
  if (cities.length === 0) return <Muted>No cities to show: no city is active in SDP (turn on log_only to see shadow cities).</Muted>;
  const byKey = new Map(rows.map((r) => [`${r.cityId}:${r.departureDate}`, r]));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" data-testid="state-grid" sx={{ borderCollapse: 'separate', borderSpacing: '3px', width: '100%' }}>
        <thead>
          <tr>
            <Box component="th" sx={{ ...headSx, textAlign: 'left', minWidth: 120 }}>
              City
            </Box>
            {days.map((day) => (
              <DayHeader key={day} day={day} today={today} />
            ))}
          </tr>
        </thead>
        <tbody>
          {cities.map((city) => {
            const cityName = city.cityName || city.cityId;
            return (
              <tr key={city.cityId} data-testid={`grid-row-${city.cityId}`}>
                <Box component="th" scope="row" sx={{ ...headSx, textAlign: 'left', fontWeight: 600, borderBottom: 'none' }}>
                  {cityName}
                  {city.mode && city.mode !== 'active' && (
                    <Typography component="span" color="text.secondary" sx={{ display: 'block', fontSize: '11px', fontWeight: 400 }}>
                      {city.mode}
                    </Typography>
                  )}
                </Box>
                {days.map((day) => (
                  <GridCell
                    key={day}
                    day={day}
                    today={today}
                    cityName={cityName}
                    row={byKey.get(`${city.cityId}:${day}`)}
                    onSelect={onSelect}
                  />
                ))}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
};

export default StateGrid;
